/**
 * Company Overview Tab Component - Company info and capabilities.
 * CR-4821: Performance Summary has been removed from this tab; all performance
 * data lives on the Performance tab (single source of truth).
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Building2,
  Mail,
  Globe,
  MapPin,
  Users,
  Calendar,
  DollarSign,
  Award,
  Clock,
  Phone,
  Hash,
  FileText,
  Pencil,
} from 'lucide-react';
import { CustomerProfile } from '../../lib/api';
import { getFullAddress } from './utils';
import { useFormatters } from '../../contexts/PreferencesContext';
import { EditCompanyDialog, CompanyEditForm } from './EditCompanyDialog';
import { toast } from 'sonner';

interface OverviewTabProps {
  customer: CustomerProfile;
  formatCurrency: (value: string | number) => string;
  canEdit: boolean;
  onUpdateProfile: (data: Partial<CustomerProfile>) => Promise<void>;
  onUpdateCompany: (data: Record<string, string | number | boolean | null>) => Promise<void>;
}

export function OverviewTab({ customer, formatCurrency, canEdit, onUpdateProfile, onUpdateCompany }: OverviewTabProps) {
  const { formatDate } = useFormatters();
  const fullAddress = getFullAddress(customer);

  // CR-4819: Company Information edit dialog lives on Overview (moved from Settings)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState<CompanyEditForm>({
    customer_code: '', registration_number: '', payment_terms: '', currency: '', annual_revenue: '', employee_count: '',
    year_established: '', primary_email: '', primary_phone: '', website: '',
    address_line_1: '', address_line_2: '', city: '', state: '', postal_code: '', country: '',
  });

  const openEditDialog = () => {
    setEditForm({
      customer_code: customer.company_details?.customer_code || '',
      registration_number: customer.company_details?.registration_number || '',
      payment_terms: customer.payment_terms || '', currency: customer.currency || '',
      annual_revenue: customer.annual_revenue || '', employee_count: customer.employee_count?.toString() || '',
      year_established: customer.year_established?.toString() || '',
      primary_email: customer.company_details?.primary_email || '',
      primary_phone: customer.company_details?.primary_phone || '',
      website: customer.company_details?.website || '',
      address_line_1: customer.company_details?.address_line_1 || '',
      address_line_2: customer.company_details?.address_line_2 || '',
      city: customer.company_details?.city || '',
      state: customer.company_details?.state || '',
      postal_code: customer.company_details?.postal_code || '',
      country: customer.company_details?.country || '',
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // CR-4815 round 2: build a real delta — only send fields the user
      // actually changed. The previous version PATCHed every dialog field on
      // every save, which:
      //   * sent currency: "" when the customer had no currency configured
      //     (CustomerProfile.currency is CharField(max_length=3) with no
      //     blank=True, so DRF rejected "" with "This field may not be
      //     blank") — that single field killed the whole CustomerProfile
      //     PATCH after the Company PATCH had already landed, producing the
      //     partial-save Jacqueline reported in round 2;
      //   * sent unchanged emails / urls back through DRF EmailField /
      //     URLField validators which sometimes flag values our model
      //     accepted historically but DRF round-trip rejects.
      const company = customer.company_details || {};
      const companyDelta: Record<string, string | number | boolean | null> = {};
      const addCompany = (key: string, current: string | undefined, formValue: string) => {
        if ((current ?? '') !== formValue) companyDelta[key] = formValue;
      };
      addCompany('customer_code', company.customer_code, editForm.customer_code);
      addCompany('registration_number', company.registration_number, editForm.registration_number);
      addCompany('primary_email', company.primary_email, editForm.primary_email);
      addCompany('primary_phone', company.primary_phone, editForm.primary_phone);
      addCompany('website', company.website, editForm.website);
      addCompany('address_line_1', company.address_line_1, editForm.address_line_1);
      addCompany('address_line_2', company.address_line_2, editForm.address_line_2);
      addCompany('city', company.city, editForm.city);
      addCompany('state', company.state, editForm.state);
      addCompany('postal_code', company.postal_code, editForm.postal_code);
      addCompany('country', company.country, editForm.country);

      const profileDelta: Partial<CustomerProfile> = {};
      if ((customer.payment_terms ?? '') !== editForm.payment_terms) {
        profileDelta.payment_terms = editForm.payment_terms;
      }
      if (editForm.currency && (customer.currency ?? '') !== editForm.currency) {
        // currency is required on CustomerProfile (no blank=True) — never
        // ship an empty string even if the user opens + closes the dropdown.
        profileDelta.currency = editForm.currency;
      }
      if (editForm.annual_revenue && (customer.annual_revenue ?? '') !== editForm.annual_revenue) {
        profileDelta.annual_revenue = editForm.annual_revenue;
      }
      const employeeCount = editForm.employee_count ? (parseInt(editForm.employee_count) || null) : null;
      if (employeeCount !== (customer.employee_count ?? null)) {
        profileDelta.employee_count = employeeCount;
      }
      const yearEstablished = editForm.year_established ? (parseInt(editForm.year_established) || null) : null;
      if (yearEstablished !== (customer.year_established ?? null)) {
        profileDelta.year_established = yearEstablished;
      }

      // Run the smaller / more constrained payload first so that if neither
      // side is going to succeed we fail fast and don't half-write Company.
      if (Object.keys(profileDelta).length > 0) {
        await onUpdateProfile(profileDelta);
      }
      if (Object.keys(companyDelta).length > 0) {
        await onUpdateCompany(companyDelta);
      }
      if (Object.keys(profileDelta).length === 0 && Object.keys(companyDelta).length === 0) {
        toast.info('No changes to save');
      } else {
        toast.success('Customer details updated successfully');
      }
      setEditDialogOpen(false);
    } catch (err: unknown) {
      // CR-4815 round 2: read the Helix error envelope (status/code/message/
      // details.fields) FIRST, then fall back to flat DRF field-error shape.
      // Without this branch, the toast collapsed every server validation
      // error to a generic "Validation failed".
      const error = err as { response?: { data?: Record<string, unknown> } };
      const data = error.response?.data as Record<string, unknown> | undefined;
      let message = 'Failed to update customer details';
      const details = data?.['details'] as Record<string, unknown> | undefined;
      const envelopeFields = details?.['fields'] as Record<string, unknown> | undefined;
      if (envelopeFields && typeof envelopeFields === 'object') {
        const fieldErrors = Object.entries(envelopeFields)
          .map(([field, v]) => `${field}: ${Array.isArray(v) ? v.join(', ') : String(v)}`)
          .join('; ');
        if (fieldErrors) message = fieldErrors;
      } else if (data && typeof data === 'object') {
        const fieldErrors = Object.entries(data)
          .filter(([, v]) => Array.isArray(v) || typeof v === 'string')
          .map(([field, v]) => `${field}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join('; ');
        if (fieldErrors) message = fieldErrors;
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        {/* Company Information */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company Information
              </CardTitle>
              {/* CR-4819: view + edit live together on Overview now */}
              {canEdit && (
                <Button variant="outline" size="sm" onClick={openEditDialog}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {customer.company_details.primary_email && (
              <div className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Email</p>
                  <p className="text-sm text-muted-foreground">
                    {customer.company_details.primary_email}
                  </p>
                </div>
              </div>
            )}
            {/* CR-4057: Telephone */}
            {customer.company_details.primary_phone && (
              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Telephone</p>
                  <p className="text-sm text-muted-foreground">
                    {customer.company_details.primary_phone}
                  </p>
                </div>
              </div>
            )}
            {/* CR-4057: Company Number */}
            <div className="flex items-start gap-3">
              <Hash className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Company Number</p>
                <p className="text-sm text-muted-foreground">
                  {customer.company_details.registration_number || 'Not specified'}
                </p>
              </div>
            </div>
            {customer.company_details.website && (
              <div className="flex items-start gap-3">
                <Globe className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Website</p>
                  <a
                    href={customer.company_details.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {customer.company_details.website}
                  </a>
                </div>
              </div>
            )}
            {fullAddress && (
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Location</p>
                  <p className="text-sm text-muted-foreground">{fullAddress}</p>
                </div>
              </div>
            )}
            {customer.employee_count && (
              <div className="flex items-start gap-3">
                <Users className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Employees</p>
                  <p className="text-sm text-muted-foreground">
                    {customer.employee_count} employees
                  </p>
                </div>
              </div>
            )}
            {customer.year_established && (
              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Established</p>
                  <p className="text-sm text-muted-foreground">{customer.year_established}</p>
                </div>
              </div>
            )}
            {customer.annual_revenue && (
              <div className="flex items-start gap-3">
                <DollarSign className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Annual Revenue</p>
                  <p className="text-sm text-muted-foreground">
                    {formatCurrency(customer.annual_revenue)}
                  </p>
                </div>
              </div>
            )}
            {/* CR-4057: Company Description */}
            {customer.company_details.description && (
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Description</p>
                  <p className="text-sm text-muted-foreground">
                    {customer.company_details.description}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CR-4821: Performance Summary removed from Company Overview;
            all performance metrics now live only on the Performance tab. */}
      </div>

      {/* Certifications */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Certifications */}
        {customer.certifications.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Certifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {customer.certifications.map((cert, idx) => (
                  <Badge key={idx} variant="outline" className="bg-blue-50 dark:bg-blue-950">
                    {cert}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* CR-4819: Company Information edit dialog (was on Settings, moved here) */}
      <EditCompanyDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        editForm={editForm}
        setEditForm={setEditForm}
        saving={saving}
        onSave={handleSave}
      />

      {/* Audit Information */}
      {(customer.last_audit_date || customer.next_audit_date) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Audit Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            {customer.last_audit_date && (
              <div>
                <p className="text-sm font-medium">Last Audit</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(customer.last_audit_date)}
                </p>
              </div>
            )}
            {customer.next_audit_date && (
              <div>
                <p className="text-sm font-medium">Next Audit</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(customer.next_audit_date)}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
